import React, {useEffect, useRef} from 'react'
import {observer} from 'mobx-react'
import {Box} from '../../../vanilla'
import {QuickLink} from './QuickLink'
import {QuickLinksCmsContent} from '../../../types/cms'
import {SlotComponent} from '../../../types/ui'
import {Carousel} from '../../carousel'
import {quickLinksDesktopContainer} from './styles.css'
import {createPromoForSelectPromotion} from '../../../analytics/utils'
import {useViewPromotionTracking} from '../../../analytics/view-promotion-tracking'

interface QuickLinksProps {
  slotGroupName?: string
  slotIndex?: number
}

const QuickLinksComponent: SlotComponent<QuickLinksCmsContent, QuickLinksProps> = ({
  content,
  slotGroupName,
  slotIndex
}) => {
  const {quickLinks, campaign} = content
  const sendViewPromotion = useViewPromotionTracking()

  // single map for both viewports, keyed as `${deliveryId}-desktop` / `${deliveryId}-mobile`
  const allRefs = useRef(new Map<string, HTMLAnchorElement>())
  const promotionData = useRef(new Map<string, object>())
  // tracks seen links across both viewports to prevent duplicate events
  const viewedLinks = useRef(new Set<string>())
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visiblePromotions = entries.flatMap((entry) => {
          const key = entry.target.getAttribute('data-quicklink-key')
          if (!entry.isIntersecting || !key || viewedLinks.current.has(key)) return []

          const promotion = promotionData.current.get(key)
          if (!promotion) return []

          viewedLinks.current.add(key)
          const desktopEl = allRefs.current.get(`${key}-desktop`)
          const mobileEl = allRefs.current.get(`${key}-mobile`)
          if (desktopEl) observerRef.current?.unobserve(desktopEl)
          if (mobileEl) observerRef.current?.unobserve(mobileEl)

          return [promotion]
        })

        if (visiblePromotions.length > 0) {
          sendViewPromotion(visiblePromotions)
        }
      },
      {rootMargin: '0px -20px 0px 0px'}
    )

    allRefs.current.forEach((element) => observerRef.current?.observe(element))

    return () => observerRef.current?.disconnect()
  }, [])

  if (!quickLinks.length) return null

  const promotions = quickLinks.map((link, index) => ({
    ...createPromoForSelectPromotion({
      campaign,
      content: link,
      slotGroupName,
      slotPosition: slotIndex
    }),
    promotion_id: link.imageLink,
    promotion_name: link.displayName,
    link_position: index + 1
  }))

  return (
    <>
      {/* Desktop View - Row */}
      <Box
        flexDirection="row"
        display={['none', 'flex']}
        width="full"
        gap="8px"
        className={quickLinksDesktopContainer}
      >
        {quickLinks.map((link, index) => {
          const key = link._meta.deliveryId
          const promotion = promotions[index]
          return (
            <QuickLink
              key={key}
              content={link}
              promotion={promotion}
              quickLinkKey={key}
              elementRef={(el) => {
                const refKey = `${key}-desktop`
                if (el) {
                  allRefs.current.set(refKey, el)
                  promotionData.current.set(key, promotion)
                  observerRef.current?.observe(el)
                } else {
                  const oldEl = allRefs.current.get(refKey)
                  if (oldEl) observerRef.current?.unobserve(oldEl)
                  allRefs.current.delete(refKey)
                }
              }}
            />
          )
        })}
      </Box>

      {/* Mobile View - Carousel */}
      <Box display={['block', 'none']}>
        <Carousel
          loop={false}
          controlConfig={{
            showDots: false,
            showArrows: {mobile: false, desktop: false, tablet: false}
          }}
          mobilePeekMode={true}
          itemClassName="quick-links-carousel-item"
        >
          {quickLinks.map((link, index) => {
            const key = link._meta.deliveryId
            const promotion = promotions[index]
            return (
              <QuickLink
                key={key}
                content={link}
                promotion={promotion}
                quickLinkKey={key}
                elementRef={(element) => {
                  const refKey = `${key}-mobile`
                  if (element) {
                    allRefs.current.set(refKey, element)
                    promotionData.current.set(key, promotion)
                    observerRef.current?.observe(element)
                  } else {
                    const oldEl = allRefs.current.get(refKey)
                    if (oldEl) observerRef.current?.unobserve(oldEl)
                    allRefs.current.delete(refKey)
                  }
                }}
              />
            )
          })}
        </Carousel>
      </Box>
    </>
  )
}

export const QuickLinks = observer(QuickLinksComponent)
